import React, { useState } from 'react';
import { Dumbbell, Mail, Lock, User } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../contexts/ThemeContext';

type AuthMode = 'login' | 'signup' | 'forgot';

export function AuthForm() {
  const { theme } = useTheme();
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError(null);
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        setMessage('Revisa tu email para confirmar tu cuenta');
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        setMessage('Te enviamos un email con el link para restablecer tu contraseña');
      }
    } catch (err: any) {
      const msg: string = err.message ?? '';
      if (msg.toLowerCase().includes('rate limit')) {
        setError('Demasiados intentos. Esperá unos minutos antes de volver a intentarlo.');
      } else if (msg.toLowerCase().includes('invalid login credentials')) {
        setError('Email o contraseña incorrectos.');
      } else if (msg.toLowerCase().includes('user already registered')) {
        setError('Ya existe una cuenta con ese email.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen ${theme.bg} ${theme.text} flex items-center justify-center p-4`}
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)',
        paddingLeft: 'calc(env(safe-area-inset-left) + 1rem)',
        paddingRight: 'calc(env(safe-area-inset-right) + 1rem)',
      }}
    >
      <div className={`w-full max-w-md ${theme.card} rounded-xl p-6 sm:p-8 border ${theme.border}`}>
        <div className="flex items-center justify-center gap-3 mb-6">
          <Dumbbell className="w-8 h-8 text-blue-500" />
          <h1 className="text-2xl font-bold">Groso.app</h1>
        </div>

        <h2 className="text-xl font-semibold text-center mb-6">
          {mode === 'login' ? 'Iniciar Sesión' : mode === 'signup' ? 'Crear Cuenta' : 'Recuperar Contraseña'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
              Email
            </label>
            <div className="relative">
              <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${theme.textSecondary}`} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.slice(0, 50))}
                required
                placeholder="tu@email.com"
                maxLength={50}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${theme.input}`}
              />
            </div>
          </div>

          {mode !== 'forgot' && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>
                Contraseña
              </label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${theme.textSecondary}`} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value.slice(0, 50))}
                  required
                  placeholder="••••••••"
                  minLength={6}
                  maxLength={50}
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border ${theme.input}`}
                />
              </div>
            </div>
          )}

          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className={`text-sm ${theme.textSecondary} hover:text-blue-500`}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {message && (
            <div className="p-3 rounded-lg bg-green-500/20 text-green-400 text-sm">
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <User className="w-5 h-5" />
                {mode === 'login' ? 'Iniciar Sesión' : mode === 'signup' ? 'Crear Cuenta' : 'Enviar email'}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          {mode === 'forgot' ? (
            <button
              onClick={() => switchMode('login')}
              className={`text-sm ${theme.textSecondary} hover:text-blue-500`}
            >
              Volver a iniciar sesión
            </button>
          ) : (
            <button
              onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
              className={`text-sm ${theme.textSecondary} hover:text-blue-500`}
            >
              {mode === 'login'
                ? '¿No tienes cuenta? Regístrate'
                : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
